import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Modal,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    Paper,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Popover,
    Alert,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { zoneService } from '../../../services/zoneService';
import { isZoneEngine } from '../../../services/rateEngine';
import ZoneForm from './ZoneForm';

const ZoneList = ({ onDataChange }) => {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [viewMode, setViewMode] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [zoneToDelete, setZoneToDelete] = useState(null);
    const [deleteImpact, setDeleteImpact] = useState(null);
    
    // Add state for popover
    const [regionPairsAnchorEl, setRegionPairsAnchorEl] = useState(null);
    const [selectedMoreInfo, setSelectedMoreInfo] = useState(null);

    // Handlers for popover
    const handlePopoverOpen = (event, data) => {
        setRegionPairsAnchorEl(event.currentTarget);
        setSelectedMoreInfo(data);
    };

    const handlePopoverClose = () => {
        setRegionPairsAnchorEl(null);
        setSelectedMoreInfo(null);
    };

    useEffect(() => {
        fetchZones();
    }, []);

    const fetchZones = async (search = '') => {
        setLoading(true);
        try {
            const response = await zoneService.getAllZones({ search });
            if (response.success) {
                setZones(response.data);
                if (onDataChange) {
                    onDataChange();
                }
            }
        } catch (error) {
            console.error('Error fetching zones:', error);
            showError('Failed to fetch zones. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Every zone is loaded (there are few), and the list below filters them by
    // name and code as the user types, so searching needs no request.
    const handleSearch = (value) => {
        setSearchText(value);
    };

    const handleAdd = () => {
        setEditingZone(null);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleEdit = (zone) => {
        setEditingZone(zone);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleView = (zone) => {
        setEditingZone(zone);
        setViewMode(true);
        setIsModalVisible(true);
    };

    const handleDeleteClick = (zone) => {
        setZoneToDelete(zone);
        setDeleteImpact(null);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async (confirm = false) => {
        if (!zoneToDelete) {
            setDeleteDialogOpen(false);
            return;
        }

        try {
            const response = await zoneService.deleteZone(zoneToDelete.id, confirm);

            // The zone still holds cities and/or rates. Show exactly what will be
            // stranded and let the user decide: a zone may well cover places the
            // business does not service.
            if (response.requires_confirmation) {
                setDeleteImpact(response.data);
                return;
            }

            if (response.success) {
                showSuccess(
                    deleteImpact?.cities > 0
                        ? `Zone deleted. ${deleteImpact.cities} city(ies) now have no zone and cannot be priced until reassigned.`
                        : 'Zone deleted successfully'
                );
                fetchZones();
            } else {
                showError(response.message || 'Failed to delete zone');
            }
        } catch (error) {
            console.error('Error deleting zone:', error);
            showError(error?.message || 'Failed to delete zone. Please try again.');
        }

        setDeleteDialogOpen(false);
        setDeleteImpact(null);
        setZoneToDelete(null);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingZone(null);
        setViewMode(false);
    };

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setEditingZone(null);
        setViewMode(false);
        fetchZones();
        const action = editingZone ? 'updated' : 'created';
        showSuccess(`Zone ${action} successfully`);
    };

    // A destination zone is described by the cities it covers; only a legacy
    // zone is described by the region pairs routing through it.
    const renderZoneCoverage = (zone) => {
        if (zone.is_system) {
            return zone.city_count > 0
                ? <Chip label={`${zone.city_count} cities`} size="small" color="primary" variant="outlined" />
                : <Chip label="No cities assigned" size="small" color="warning" />;
        }

        return renderRegionPairs(zone.region_pairs);
    };

    const renderRegionPairs = (regionPairs) => {
        if (!regionPairs || regionPairs.length === 0) {
            return <Chip label="No region pairs" size="small" />;
        }

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {regionPairs.slice(0, 2).map((pair, index) => (
                    <Chip 
                        key={index}
                        label={`${pair.from_region.name} ↔ ${pair.to_region.name}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                ))}
                {regionPairs.length > 2 && (
                    <>
                        <Chip 
                            label={`+${regionPairs.length - 2} more`}
                            size="small"
                            variant="outlined"
                            onClick={(e) => handlePopoverOpen(e, regionPairs.slice(2))}
                            sx={{ cursor: 'pointer' }}
                        />
                        <Popover
                            open={Boolean(regionPairsAnchorEl)}
                            anchorEl={regionPairsAnchorEl}
                            onClose={handlePopoverClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                            }}
                            PaperProps={{
                                sx: {
                                    p: 2,
                                    maxWidth: 400,
                                    boxShadow: 2,
                                }
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Additional Region Pairs</Typography>
                            <Stack spacing={1}>
                                {selectedMoreInfo?.map((pair, index) => (
                                    <Chip
                                        key={index}
                                        label={`${pair.from_region.name} ↔ ${pair.to_region.name}`}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        sx={{ justifyContent: 'flex-start' }}
                                    />
                                ))}
                            </Stack>
                        </Popover>
                    </>
                )}
            </Box>
        );
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredZones = zones.filter(zone => 
        zone.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (zone.code && zone.code.toLowerCase().includes(searchText.toLowerCase()))
    );

    const paginatedZones = filteredZones.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <TextField
                    placeholder="Search zones..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="small"
                    sx={{ width: 300 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add New Zone
                </Button>
            </Stack>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>Zone Name</TableCell>
                            <TableCell>Zone Code</TableCell>
                            <TableCell>{isZoneEngine() ? 'Cities Covered' : 'Region Pairs'}</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : paginatedZones.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography color="text.secondary">
                                        No zones found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedZones.map((zone, index) => (
                                <TableRow key={zone.id}>
                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                    <TableCell>
                                        {zone.name}
                                        {zone.is_default && (
                                            <Chip label="Default" size="small" variant="outlined" sx={{ ml: 1 }} />
                                        )}
                                    </TableCell>
                                    <TableCell>{zone.code || '-'}</TableCell>
                                    <TableCell>{renderZoneCoverage(zone)}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Details">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleView(zone)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Zone">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleEdit(zone)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            {/* South, West, North and East always exist; the
                                                server refuses to delete them too. */}
                                            {!zone.is_default && (
                                                <Tooltip title="Delete Zone">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(zone)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredZones.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Edit/Add Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="zone-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography id="zone-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        {viewMode ? 'View' : editingZone ? 'Edit' : 'Add'} Zone
                    </Typography>
                    <ZoneForm
                        zone={editingZone}
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                        viewMode={viewMode}
                    />
                </Box>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete {zoneToDelete?.name}
                </DialogTitle>
                <DialogContent>
                    {deleteImpact ? (
                        <>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                This zone is still in use. Deleting it will:
                            </Alert>
                            <List dense>
                                {deleteImpact.cities > 0 && (
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={`Leave ${deleteImpact.cities} city(ies) with no zone`}
                                            secondary="The cities are kept, but bookings to them cannot be priced until each is assigned another zone."
                                        />
                                    </ListItem>
                                )}
                                {(deleteImpact.default_rates > 0 || deleteImpact.band_rates > 0) && (
                                    <ListItem disableGutters>
                                        <ListItemText
                                            primary={`Retire ${deleteImpact.default_rates} default and ${deleteImpact.band_rates} weight-band rate(s)`}
                                            secondary="They are marked deleted rather than removed, so they remain recoverable in the database."
                                        />
                                    </ListItem>
                                )}
                            </List>
                            {deleteImpact.sample_cities?.length > 0 && (
                                <Box sx={{ mt: 1, mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Affected cities include:{' '}
                                        {deleteImpact.sample_cities.join(', ')}
                                        {deleteImpact.cities > deleteImpact.sample_cities.length &&
                                            ` and ${deleteImpact.cities - deleteImpact.sample_cities.length} more`}
                                    </Typography>
                                </Box>
                            )}

                            {/* Naming the prices that disappear. A count alone
                                does not tell an admin what they are losing. */}
                            {deleteImpact.products?.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                        These package prices will be erased
                                    </Typography>
                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 260 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Package</TableCell>
                                                    <TableCell>Default band</TableCell>
                                                    <TableCell align="right">Rate</TableCell>
                                                    <TableCell>Weight bands</TableCell>
                                                    <TableCell align="right">Bookings</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {deleteImpact.products.map((p) => (
                                                    <TableRow key={p.product_id}>
                                                        <TableCell>
                                                            {p.name}
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {p.code}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{p.default_band || '-'}</TableCell>
                                                        <TableCell align="right">
                                                            {p.default_rate != null ? `₹${p.default_rate}` : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {p.bands?.length > 0
                                                                ? p.bands.map((b, i) => (
                                                                    <Typography key={i} variant="caption" display="block">
                                                                        {b.range}: ₹{b.rate}
                                                                    </Typography>
                                                                ))
                                                                : '-'}
                                                        </TableCell>
                                                        <TableCell align="right">{p.bookings}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        These packages will no longer price anything for this zone. Bookings
                                        to its cities will be refused until the cities are reassigned and the
                                        new zone has rates.
                                    </Typography>
                                </>
                            )}
                        </>
                    ) : (
                        <Typography>
                            Are you sure you want to delete the zone "{zoneToDelete?.name}"?
                            This action cannot be undone.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDeleteDialogOpen(false); setDeleteImpact(null); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleDeleteConfirm(Boolean(deleteImpact))}
                        color="error"
                        variant="contained"
                    >
                        {deleteImpact ? 'Delete anyway' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoneList; 